import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { reportClientError } from "@/lib/clientErrorLogger";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Helencia ErrorBoundary]", error, info?.componentStack);
    this.setState({ info });
    reportClientError({
      type: "render",
      message: error?.message || String(error),
      stack: error?.stack || "",
      component_stack: info?.componentStack || "",
      url: typeof window !== "undefined" ? window.location.href : "",
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  goHome = () => {
    this.reset();
    window.location.href = "/";
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FFFBF5] px-6"
        data-testid="error-boundary"
      >
        <div className="max-w-lg w-full bg-white rounded-3xl border border-[#EADDC9] p-10 text-center shadow-[0_20px_60px_-30px_rgba(198,142,89,0.25)]">
          <div className="w-14 h-14 rounded-full bg-[#FAF3EA] flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-[#C68E59]" />
          </div>
          <p className="caption-label text-[#A26F3E]">Helencia</p>
          <h1 className="font-serif text-3xl text-[#1A1A1A] mt-2 leading-tight">
            Un instant, quelque chose n'a pas fonctionné
          </h1>
          <p className="text-sm text-[#6E4B2A] mt-3 leading-relaxed">
            Notre équipe a été notifiée. Vous pouvez recharger la page ou
            revenir à l'accueil — votre session reste active.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-8">
            <Button
              onClick={this.reload}
              className="flex-1 rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white h-11"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Recharger
            </Button>
            <Button
              onClick={this.goHome}
              variant="outline"
              className="flex-1 rounded-full border-[#C68E59] text-[#6E4B2A] hover:bg-[#FAF3EA] h-11"
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4 mr-2" /> Accueil
            </Button>
          </div>
          {this.state.error && (
            <details className="mt-6 text-left bg-[#FAF3EA] rounded-2xl p-4 border border-[#EADDC9]" data-testid="error-details">
              <summary className="text-xs font-semibold text-[#6E4B2A] cursor-pointer select-none">
                Détails techniques (à envoyer si le problème persiste)
              </summary>
              <pre className="mt-3 text-[11px] text-[#1A1A1A] whitespace-pre-wrap break-words leading-snug">
                {String(this.state.error?.message || this.state.error)}
                {this.state.info?.componentStack ? "\n\nComponent stack:\n" + this.state.info.componentStack.split("\n").slice(0, 8).join("\n") : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
